import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, X, Loader2 } from "lucide-react";
import api from "@/utils/api";

const VerifyEmailPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [message, setMessage] = useState('');
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const verifyEmail = async () => {
      // Get token from URL params
      const token = searchParams.get('token');
      
      console.log('VerifyEmailPage: Token from URL:', token ? 'found' : 'not found');

      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email and try again.');
        return;
      }

      try {
        console.log('VerifyEmailPage: Sending verification request...');
        const response = await api.post('/auth/verify-email', { token });
        console.log('VerifyEmailPage: Verification successful');
        
        // Save the token if returned
        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
        }
        
        setStatus('success');
        setMessage('Your email has been verified successfully!');
        
        // Redirect to home after 3 seconds
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      } catch (error) {
        console.error('VerifyEmailPage: Verification error:', error);
        setStatus('error');
        const errorDetail = error.response?.data?.detail || 'Verification failed. The link may have expired.';
        setMessage(errorDetail);
      }
    };

    verifyEmail();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardContent className="p-8 text-center">
          {status === 'verifying' && (
            <>
              <div className="relative mx-auto mb-6">
                <Loader2 className="h-16 w-16 text-emerald-500 animate-spin mx-auto" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-2">Verifying your email...</h2>
              <p className="text-slate-500">Please wait while we confirm your email address.</p>
            </>
          )}
          
          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="h-10 w-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-emerald-600 mb-3">Email Verified!</h2>
              <p className="text-slate-600 mb-4">{message}</p>
              <div className="bg-emerald-50 rounded-lg p-3">
                <p className="text-sm text-emerald-700">Redirecting you to BidZuu...</p>
              </div>
            </>
          )}
          
          {status === 'error' && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <X className="h-10 w-10 text-red-500" />
              </div>
              <h2 className="text-2xl font-bold text-red-600 mb-3">Verification Failed</h2>
              <p className="text-slate-600 mb-6">{message}</p>
              <div className="space-y-3">
                <Button 
                  onClick={() => window.location.href = '/'}
                  className="w-full bg-emerald-500 hover:bg-emerald-600"
                >
                  Go to BidZuu
                </Button>
                <p className="text-sm text-slate-500">
                  Need a new verification link? Log in and request one from your profile.
                </p>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
